/**
 * WizardStepReview
 *
 * Step 5 of the launchpad creation wizard.
 * Shows validation results before navigating to the dashboard.
 */

import React, { useEffect, useState } from 'react';
import { FeatherCheck, FeatherX, FeatherAlertTriangle } from '@subframe/core';
import { Button } from '../../../ui/components/Button';
import { LoadingSpinner } from '../../ui/LoadingSpinner';
import {
  validateLaunch,
  type LaunchValidationResult,
  type ValidationCheck,
} from '../../../api/launches';

export interface WizardStepReviewProps {
  launchId: string;
  onError?: (error: string) => void;
}

export const WizardStepReview: React.FC<WizardStepReviewProps> = ({
  launchId,
  onError,
}) => {
  const [validationResult, setValidationResult] = useState<LaunchValidationResult | null>(null);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Run validation on mount
  useEffect(() => {
    runValidation();
  }, [launchId]);

  const runValidation = async () => {
    try {
      setValidating(true);
      setValidationError(null);
      const result = await validateLaunch(launchId);
      setValidationResult(result);
    } catch (err) {
      console.error('Failed to validate launch:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to validate launch';
      setValidationError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setValidating(false);
    }
  };

  const renderCheckIcon = (check: ValidationCheck) => {
    switch (check.status) {
      case 'pass':
        return <FeatherCheck className="w-4 h-4 text-white" />;
      case 'warn':
        return <FeatherAlertTriangle className="w-4 h-4 text-white" />;
      case 'fail':
        return <FeatherX className="w-4 h-4 text-white" />;
    }
  };

  const getCheckIconBgClass = (check: ValidationCheck) => {
    switch (check.status) {
      case 'pass':
        return 'bg-success-600';
      case 'warn':
        return 'bg-warning-500';
      case 'fail':
        return 'bg-error-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-heading-2 font-heading-2 text-default-font mb-2">
          Almost Done!
        </h3>
        <p className="text-body font-body text-subtext-color">
          Review your collection setup before continuing to the dashboard.
        </p>
      </div>

      {/* Validation Results */}
      <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-body-bold font-body-bold text-default-font">
            Pre-Launch Checklist
          </h4>
          <Button
            variant="neutral-tertiary"
            size="small"
            onClick={runValidation}
            disabled={validating}
          >
            {validating ? 'Validating...' : 'Re-validate'}
          </Button>
        </div>

        {validating ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" />
            <span className="ml-3 text-body font-body text-subtext-color">
              Running validation checks...
            </span>
          </div>
        ) : validationError ? (
          <div className="p-4 rounded-lg bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800">
            <p className="text-body font-body text-error-600 dark:text-error-400">
              {validationError}
            </p>
          </div>
        ) : validationResult ? (
          <div className="space-y-4">
            {/* Summary */}
            <div className="flex items-center justify-between p-4 rounded-lg border border-neutral-border bg-neutral-100">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-body-bold font-body-bold bg-success-600 text-white">
                    {validationResult.summary.passed}
                  </span>
                  <span className="text-body font-body text-default-font">passed</span>
                </div>
                {validationResult.summary.warnings > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-body-bold font-body-bold bg-warning-500 text-white">
                      {validationResult.summary.warnings}
                    </span>
                    <span className="text-body font-body text-default-font">warnings</span>
                  </div>
                )}
                {validationResult.summary.failures > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-body-bold font-body-bold bg-error-600 text-white">
                      {validationResult.summary.failures}
                    </span>
                    <span className="text-body font-body text-default-font">failures</span>
                  </div>
                )}
              </div>
              <div>
                {validationResult.valid ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-body-bold font-body-bold bg-success-600 text-white">
                    <FeatherCheck className="w-4 h-4" />
                    Ready for submission
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-body-bold font-body-bold bg-warning-500 text-white">
                    <FeatherAlertTriangle className="w-4 h-4" />
                    Review warnings/failures
                  </span>
                )}
              </div>
            </div>

            {/* Checks List */}
            <div className="space-y-2">
              {validationResult.checks.map((check) => (
                <div
                  key={check.id}
                  className="flex items-start gap-4 p-4 rounded-lg border border-neutral-border"
                >
                  <span
                    className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${getCheckIconBgClass(
                      check
                    )}`}
                  >
                    {renderCheckIcon(check)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-body-bold font-body-bold text-default-font">
                      {check.name}
                    </span>
                    <p className="text-body font-body text-subtext-color mt-1">{check.message}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {/* Next Steps */}
      <div className="p-6 rounded-lg border border-neutral-border bg-neutral-100">
        <h4 className="text-body-bold font-body-bold text-default-font mb-2">
          Next Steps
        </h4>
        <ul className="list-disc list-inside text-body font-body text-default-font space-y-1">
          <li>Review and fix any warnings or failures shown above</li>
          <li>Configure allowlist addresses from the dashboard (if enabled)</li>
          <li>Submit your collection for approval when ready</li>
          <li>Complete the authorization and payment steps</li>
        </ul>
      </div>

    </div>
  );
};
